import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import 'react-image-lightbox/style.css';
import { extractFirstPageAsImage } from 'src/components/Preview/PDFRenderer';
import { Preview } from 'src/components/Preview/Preview';
import { PreviewController } from 'src/components/Preview/PreviewController';

type ImageGalleryWrapperProps = {
    containerRef: any;
};

const PENDING_PDF_SELECTOR =
    '.formio-component .file[data-type="application/pdf"]:not(.processed)';

/**
 * Paints the first page of every PDF tile that has no thumbnail yet.
 *
 * Tiles are claimed with `processed` *before* the render starts: a sweep can be
 * triggered again while pdf.js is still working, and the same file must not be
 * rendered twice. A tile whose render fails stays claimed — retrying it on every
 * later mutation would loop.
 */
const renderPdfThumbnails = async () => {
    const files = Array.from(document.querySelectorAll(PENDING_PDF_SELECTOR));

    await Promise.all(
        files.map(async (file) => {
            file.classList.add('processed');

            const image = await extractFirstPageAsImage(
                file.getAttribute('data-url') as string,
            );
            const thumbnail =
                file.querySelector<HTMLElement>('.file__thumbnail');

            if (image && thumbnail) {
                thumbnail.style.backgroundImage = `url(${image})`;
            }
        }),
    );
};

export const ImageGalleryWrapper = ({
    containerRef,
}: ImageGalleryWrapperProps) => {
    useEffect(() => {
        // Form.io usually has the tiles in the DOM before this effect runs, so
        // there is no mutation left for the observer to see — sweep once up front
        // (LBOR-69: without this the thumbnails never appeared at all). The
        // observer then covers tiles added later, e.g. after an upload.
        renderPdfThumbnails();

        const mo = new MutationObserver(_.debounce(renderPdfThumbnails, 200));

        mo.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => {
            mo.disconnect();
        };
    }, []);

    const onFileClick = useMemo(() => {
        return getOnFileClick(window.document.body);
    }, []);

    useEffect(() => {
        window.document.body.addEventListener('click', onFileClick as any);

        return () => {
            window.document.body.removeEventListener(
                'click',
                onFileClick as any,
            );
        };
    }, [containerRef.current]);

    return <Preview />;
};

const getOnFileClick =
    (current: HTMLElement) => (e: React.MouseEvent<HTMLElement>) => {
        const el = (e.target as HTMLElement).closest('.file');

        if (el && (e.target as HTMLElement).tagName !== 'A') {
            const allFiles = Array.from(
                current.querySelectorAll('.formio-component-file .file'),
            );

            PreviewController.show(
                allFiles.map((fileEl) => ({
                    uri: fileEl.getAttribute('data-url'),
                    name: fileEl.getAttribute('data-name'),
                    originalName: fileEl.getAttribute('data-original-name'),
                })),
                allFiles.indexOf(el),
            );
        }
    };
